import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { MaskedInput, TextInput } from "components/inputs";
import { useEffect } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetClientByIdQuery } from "services/client-api";

type ClientInformationProps = {
  onSave: (field: string, value: unknown) => Promise<void>;
};

const ClientInformation = ({ onSave }: ClientInformationProps) => {
  const { clientId } = useParams<{ clientId?: string }>();

  const { data: client } = useGetClientByIdQuery(
    { id: clientId },
    { skip: !clientId },
  );

  const { setError } = useFormContext();
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      client_id: "",
      email: "",
      custom_bill_rate_adjustments: null,
      owner: "",
      owner_email: "",
      owner_phone: "",
    },
  });

  useEffect(() => {
    if (client) {
      reset(
        {
          name: client.name || "",
          client_id: client.client_id || "",
          email: client.email || "",
          custom_bill_rate_adjustments: client.custom_bill_rate_adjustments,
          owner: client.owner || "",
          owner_email: client.owner_email || "",
          owner_phone: client.owner_phone || "",
        },
        {
          keepDirtyValues: true,
        },
      );
    }
  }, [client, reset]);

  const handleSave = async (field: string, value: unknown) => {
    try {
      await onSave(field, value);
    } catch (error) {
      if (error.data?.[field]) {
        setError(field, {
          type: "manual",
          message: error.data[field],
        });
      }
    }
  };

  return (
    <Card heading="Client Information" style={{ marginBottom: "10px" }}>
      <ControlledInput
        control={control}
        name="name"
        label="Name"
        errors={errors}
        onSave={(value) => handleSave("name", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="client_id"
        label="Client ID"
        errors={errors}
        onSave={(value) => handleSave("client_id", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="email"
        label="Email"
        errors={errors}
        onSave={(value) => handleSave("email", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="custom_bill_rate_adjustments"
        label="Custom Bill Rate Adjustments"
        errors={errors}
        onSave={(value) => handleSave("custom_bill_rate_adjustments", value)}
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="owner"
        label="Owner"
        errors={errors}
        onSave={(value) => handleSave("owner", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="owner_email"
        label="Owner Email"
        errors={errors}
        onSave={(value) => handleSave("owner_email", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="owner_phone"
        label="Owner Phone"
        errors={errors}
        onSave={(value) => handleSave("owner_phone", value)}
        componentType={MaskedInput}
        mask="phone"
      />
    </Card>
  );
};

export default ClientInformation;
